<template>
	<v-row class="pb-2 ma-0" id="layout">
		<template v-for="(field, i) in showFields">
			<transition name="expansion-fade" :key="i">
				<v-col v-if="checkDepends(field)" 
					v-show="isFieldVisible(field)" 
					:class="field.class ? field.class : 'col-12 col-sm-6 col-md-6 col-lg-6 px-6'" 
					class="py-0 disabled expansion-field">
				<template v-if="field.type === 'text-editor' ">
					<v-subheader class="pl-0">{{field.label}}</v-subheader>
					<template v-if="forceTinymce || $store.getters.checkSettings('use_tinymce_editor')">
						<tinymce-editor
							:editor-id="'text-editor' + i"
							v-model="model[field.key]"
							ref="tinymceEditor"
						/>
					</template>
					<vue-editor v-else
						:id="'text-editor' + i"
						v-model="model[field.key]"
						ref="editor"
						:editorOptions="editorSettings"
						:editorToolbar="customToolbar"
						useCustomImageHandler
						:data-vv-name="field.label"
						v-validate="field.validations ? field.validations : ''"
						:error-messages="errors.collect(field.label)"
						@image-added="handleImageAdded"
						:class="{'vue-editor': false, error:errors.has(field.label)}"
					></vue-editor>
					<div class="ml-3 caption error--text">{{errors.collect(field.label)[0]}}</div>
				</template>

				<template v-else-if="field.type === 'textarea' || field.type === 'multiline'">
					<v-textarea
						:label="field.required ? field.label + '*' : field.label"
						v-model="model[field.key]"
						:ref="field.key"
						:data-vv-name="field.label"
						v-validate="field.validations ? field.validations : ''"
						:hint="field.hint"
						:persistent-hint="field.persistent_hint"
						outlined
					></v-textarea>
				</template>
				<template v-else-if="field.type === 'code-editor'">
					<code-editor
						:value="model[field.key]"
						@input="model[field.key] = $event"
						:label="field.required ? field.label + '*' : field.label"
						:hint="field.hint"
						:persistent-hint="field.persistent_hint"
						:language="field.language || 'sql'"
						:rows="field.rows || 10"
						:required="field.required"
						:placeholder="field.placeholder"
						outlined
					/>
				</template>
				<template v-else-if="field.type === 'whatsappTemplate'">
					<whats-app-template-field
						v-model="model[field.key]"
						:label="field.required ? field.label + '*' : field.label"
						:placeholder="field.placeholder"
						:prepend-icon="field.prepend_icon"
						:required="field.required"
						:show-preview="field.show_preview !== false"
						:show-variables-mapping="field.show_variables_mapping !== false"
					/>
				</template>
				<template v-else-if="field.type === 'template' && field.template_type == 'signature'">
					<v-select
						:items="field.options"
						:label="field.required ? field.label + '*' : field.label"
						:hint="field.hint"
						:persistent-hint="field.persistent_hint"
						:data-vv-name="field.label"
						v-validate="field.validations ? field.validations : ''"
						:required="field.required"
						:value="model[field.key]"
						@change="updateSignature(field, $event)"
						:item-disabled="field.disabled_key ? field.disabled_key : 'barred'"
						:ref="field.key"
						return-object
						:menu-props="{'offset-y': true}"
						clearable
						outlined
						dense>
					</v-select>
				</template>
				<v-autocomplete
					autocomplete="no"
					v-else-if="['belongsTo','generalTypes','morphTo','customBelongsTo', 'select', 'enum', 'set'].includes(field.type)"
					:label="field.required ? field.label + '*' : field.label"
					:items="field.options"
					:hint="field.hint"
					:persistent-hint="field.persistent_hint"
					v-model="model[field.key]"
					:item-text="field.item_text ? field.item_text : 'text' "
					:item-value="field.item_value ? field.item_value : 'value'"
					:multiple="field.multiple"
					:menu-props="{maxHeight:'200', nudgeBottom: 3}"
					:return-object="field.return_object"
					:prepend-icon="field.prepend_icon"
					clearable
					:error-messages="errors.collect(field.label)"
					v-validate="formatValidation(field)"
					:data-vv-validate-on="field['data-vv-validate-on'] ? field['data-vv-validate-on'] : 'input'"
					:data-vv-delay="field['data-vv-delay'] ? field['data-vv-delay'] : 0"
					:data-vv-name="field.label"
					:disabled="checkDisable(field)"
					:auto-select-first="true"
					:search-input.sync="search[field.key]"
					@change="selectChange(field, $event)"
					:cache-items="!!field.async"
					:item-disabled="(item) => item.is_divider || (field.disabled_key ? item[field.disabled_key] : item.barred)"
					:ref="field.key"
					:filter="(item, queryText, itemText) => $eventBusUtility.customFilter(item, queryText, itemText, field.searchKeys)"
					outlined dense
					:loading="field.searching"
					>
					<template v-slot:append-outer v-if="((model[field.key] && field.showModelDef) || field.advanced_search)">
						<v-tooltip top v-if="model[field.key] && field.showModelDef">
							<template v-slot:activator="{ on }">					
								<v-btn icon color="primary" @click.native="openMiniShow(field)" v-on="on">
									<v-icon>remove_red_eye</v-icon>
								</v-btn>
							</template>
							<span>Dettagli</span>
						</v-tooltip>
						<v-tooltip top v-if="field.advanced_search">
							<template v-slot:activator="{ on }">					
								<v-btn icon color="primary" @click.native="openAdvancedSearch(field)" v-on="on" class="ml-1">
									<v-icon>search</v-icon>
								</v-btn>
							</template>
							<span>Ricerca Avanzata</span>
						</v-tooltip>
					</template>
					
					<template v-if="field.select_all" v-slot:append-item>
						<v-divider
							class="mt-1"
						></v-divider>
						<v-list-item
							ripple
							@click="toggle(field)"
							>
							<v-list-item-action class="pl-2" v-if="model[field.key] && model[field.key].length == field.options.length">
								<v-icon :color="field.options.length > 0 ? 'primary' : ''">check_box</v-icon>
							</v-list-item-action>					
							<v-list-item-action class="pl-2" v-else-if="model[field.key] && model[field.key].length > 0">
								<v-icon :color="field.options.length > 0 ? 'primary' : ''">indeterminate_check_box</v-icon>
							</v-list-item-action>					
							<v-list-item-action class="pl-2" v-else>
								<v-icon :color="field.options.length > 0 ? 'primary' : ''">check_box_outline_blank</v-icon>
							</v-list-item-action>
							<v-list-item-title>Seleziona tutti</v-list-item-title>
						</v-list-item>
					</template>

					<template v-slot:selection="data">
						<v-chip
							v-bind="data.attrs"
							:input-value="data.selected"
							:close="field.close"
							:multiple="field.multiple"
							small
							pill
							@click="data.select"
							@click:close="removeChip(data.item, field)">
							<v-avatar v-if="data.item.avatar" left>
								<v-img :src="data.item.avatar"></v-img>
							</v-avatar>
							<v-avatar v-else-if="getIcon(data.item)"
								small
								pill
								style="min-width: 24px !important; width: 24px !important; height: 24px !important; border-radius: 50%"
								class="mr-4 v-avatar--left"
								:color="data.item.color ? data.item.color : 'primary'">
								<v-icon class="fs16i" color="white">{{ getIcon(data.item) }}</v-icon>
							</v-avatar>				
							<v-avatar v-else-if="field.chips"
								left
								style="height: 24px !important; width: 24px !important"
								class="white--text"
								:color="data.item.color ? data.item.color : 'primary'">
								{{ getInitialAvatar(data, field)}}
							</v-avatar>
							<div class="xs3 string-cutter" :style="{'max-width': getWidth(field.key)}">
								{{ data.item.code ? ' (' + data.item.code + ')' : '' }} {{ field.item_text ? data.item[field.item_text] : data.item.text }}
							</div>
						</v-chip>
					</template>

					<template v-slot:item="data">
						<!-- Divider row -->
						<template v-if="data.item.is_divider">
							<v-list-item class="divider-item background-1" disabled>
								<v-list-item-content>
									<v-list-item-title class="text-caption text-center">
										{{ data.item.text }}
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</template>
						<!-- Regular item -->
						<template v-else-if="typeof data.item !== 'object'">
							<v-list-item-content v-text="data.item"></v-list-item-content>
						</template>
						<template v-else>
							<v-list-item-avatar v-if="getIcon(data.item)"
								size="32px"
								style="min-width: 32px; width: 32px; height:32px; border-radius: 50%"
								class="mr-4"
								:color="data.item.color ? data.item.color : 'primary'">
								<v-icon class="fs18i" :color="data.item.color ? data.item.color : 'white'">{{ getIcon(data.item) }}</v-icon>
							</v-list-item-avatar>
							<v-list-item-avatar v-else-if="data.item.avatar" style="margin-right: 10px;">
								<v-avatar
									class="custom-avatar"
									:size="34"
									:style="{'background-image': 'url(' + data.item.avatar + ')'}">
								</v-avatar>
							</v-list-item-avatar>
							<v-list-item-avatar v-else-if="field.chips"
								size="34px"
								style="min-width: 34px; width: 34px; height:34px; border-radius: 50%"
								class="primary mr-4">
								<span style="color: white !important" class="margin-auto">{{ getInitialAvatar(data, field)}}</span>
							</v-list-item-avatar>
							<v-list-item-content>
								<v-list-item-title>{{ field.item_text ? data.item[field.item_text] : data.item.text }}</v-list-item-title>
								<v-list-item-subtitle>
									<span v-for="(key, k) in field.searchKeys" :key="k" :class="getTextClass(key)">{{$trans(data.item[key])}}&nbsp;
									</span>
								</v-list-item-subtitle>
							</v-list-item-content>
						</template>
					</template>

					<template v-slot:no-data>
						<v-list-item>
							<v-list-item-content>
								<v-list-item-title v-if="search[field.key] && (!!!field.async || !!!field.searching)">
									Nessun risultato per "<strong>{{ search[field.key] }}</strong>".
									<v-btn v-if="field.add_new && field.add_new == true" text right x-small style="margin: 0px; float: right" color="primary" @click="addNew(field)">Crea Nuovo</v-btn>
								</v-list-item-title>
								<v-list-item-title v-else-if="field.async">
									Cerca...
								</v-list-item-title>
								<v-list-item-title v-else>
									Nessun risultato
								</v-list-item-title>
							</v-list-item-content>
						</v-list-item>
						<!-- Import da cataloghi esterni: una riga dedicata per catalogo (estendibile, es. Sonepar) -->
						<v-list-item v-if="mefImportEnabled && field.key === 'product_id' && mefImport && search[field.key] && (!!!field.async || !!!field.searching)" class="py-1">
							<v-btn text x-small block color="info" :loading="field.mef_importing" @click="mefImport(field, search[field.key])">
								<v-icon left x-small>mdi-database-import</v-icon>
								Importa da catalogo MEF
							</v-btn>
						</v-list-item>
					</template>

				</v-autocomplete>

				<v-autocomplete v-else-if="field.type === 'calendar'"
					v-model="model[field.key]"
					:items="field.items"
					:label="field.required ? field.label + '*' : field.label"
					dense
					outlined
					background-color="background-2">
					<template v-slot:item="data">
						<v-list-item-avatar class="ml-n4 mr-0">
							<v-icon :color="data.item.color">fiber_manual_record</v-icon>
						</v-list-item-avatar>
						<v-list-item-content>
							<v-list-item-title>{{ data.item.text }}</v-list-item-title>
							<v-list-item-subtitle v-html="data.item.type"></v-list-item-subtitle>
						</v-list-item-content>
					</template>
				</v-autocomplete>


				<v-combobox v-else-if="field.type === 'chips'"
					:label="field.required ? field.label + '*' : field.label"
					:items="field.items"
					v-model="model[field.key]"
					:multiple="field.multiple"
					:error-messages="errors.collect(field.label)"
					v-validate="field.validations ? field.validations : ''"
					:data-vv-name="field.label"
					dense
					:hint="field.hint"
					:persistent-hint="field.persistent_hint"
					return-object
					small-chips
					outlined
					:ref="field.key">
					<template v-slot:selection="data" v-if="field.edit_chips">
						<v-chip small
						:key="JSON.stringify(data.item)"
						v-bind="data.attrs"
						:input-value="data.selected"
						:disabled="data.disabled"
						@click:close="data.parent.selectItem(data.item)">
						{{ typeof data.item == 'string' ? data.item : data.item.text }}
						<v-btn
							icon
							small
							class="ml-1"
							@click="editChip(field, data)">
							<v-icon style="font-size: 20px">
								mdi-pencil
							</v-icon>
						</v-btn>
						</v-chip>
					</template>
				</v-combobox>

				<!-- Header del gruppo collassabile -->
				<template v-else-if="field.type === 'label' && field.start">
					<div v-if="field.collapsible" 
						class="expansion-panel-header mb-4"
						:class="{ 
							'expansion-panel-header--expanded': groupsExpanded[field.group_id],
							'elevation-1': !groupsExpanded[field.group_id],
							'elevation-2': groupsExpanded[field.group_id],
							'theme--light': !$vuetify.theme.dark,
						}"
						:style="getGroupHeaderStyle(field)"
						@click="toggleGroup(field.group_id)"
						@keydown="handleKeyboardNavigation($event, field.group_id)"
						:data-group-id="field.group_id"
						tabindex="0"
						role="button"
						:aria-expanded="groupsExpanded[field.group_id] ? 'true' : 'false'"
						:aria-controls="`panel-content-${field.group_id}`"
						:aria-label="`${field.label}, ${groupsExpanded[field.group_id] ? 'espanso' : 'collassato'}, ${getGroupFieldCount(field.group_id)} campi`">
						<div class="d-flex align-center">
							<span class="expansion-panel-title" :style="getTitleStyle(field)">{{ field.label }}</span>
							<v-tooltip bottom v-if="field.description" class="ml-2">
								<template v-slot:activator="{ on, attrs }">
									<v-icon 
										v-bind="attrs"
										v-on="on"
										small
										class="ml-1">
										mdi-information-outline
									</v-icon>
								</template>
								<span>{{ field.description }}</span>
							</v-tooltip>
						</div>
						<!-- Badge con numero campi e controlli -->
						<v-spacer></v-spacer>
						<div class="d-flex align-center">
							<v-chip 
								x-small 
								class="mr-2"
								color="primary"
								text-color="white"
								v-if="getGroupFieldCount(field.group_id) > 0">
								{{ getGroupFieldCount(field.group_id) }}
							</v-chip>
							<v-icon class="expansion-panel-icon"
								:class="{ 'expansion-panel-icon--rotated': groupsExpanded[field.group_id] }"
								:style="getTitleStyle(field)">
								mdi-chevron-down
							</v-icon>
						</div>
					</div>
					<v-subheader v-else class="pl-0">
						{{field.label}}
					</v-subheader>
				</template>
				
				<template v-else-if="field.type === 'label' && field.end && !field.group_id">
					<v-divider></v-divider>
				</template>

				<template v-else-if="field.type === 'label' && field.end && field.group_id">
				</template>
				
				<v-subheader v-else-if="field.type === 'label'" class="pl-0">
					{{field.label}}
				</v-subheader>

				<v-alert v-else-if="field.type === 'info'"
					:type="field.alert_type || 'info'"
					:color="field.color || undefined"
					:icon="field.icon || undefined"
					:prominent="field.prominent || false"
					:outlined="field.outlined || false"
					:text="field.text_style !== false"
					:border="field.border || undefined"
					:dense="field.dense !== false"
					class="mb-0 mt-2 text-body-2">
					<div v-html="field.text"></div>
				</v-alert>

				<template v-else-if="field.type === 'oauth_button'">
					<v-alert text dense color="info" icon="mdi-link-variant" class="mb-0 mt-2">
						<div class="d-flex align-center" style="gap:12px;flex-wrap:wrap;">
							<span v-if="field.disabled_message && !field.enabled" class="body-2">{{ field.disabled_message }}</span>
							<v-btn v-if="field.enabled" color="primary" small @click="oauthRedirect(field)" :loading="oauthLoading[field.key]">
								<v-icon left small>mdi-open-in-new</v-icon>
								{{ field.label }}
							</v-btn>
							<v-chip v-if="field.connected" small color="success" outlined>
								<v-icon left small>mdi-check-circle</v-icon>
								{{ field.connected_label || 'Collegato' }}
							</v-chip>
						</div>
					</v-alert>
				</template>

				<template v-else-if="field.type === 'checkbox'">
					<v-checkbox v-if="!multipleEdit"
						class="ma-auto"
						:label="field.label"
						v-model="model[field.key]"
						color="primary"
						:input-value="[true,false]"
						:ref="field.key"
						:persistent-hint="field.persistent_hint"
						:hint="field.hint"
					></v-checkbox>
					<v-select
						v-else
						:items="[{'text': 'Si', 'value': true}, {'text': 'No', 'value': false}]"
						:label="field.label"
						:hint="field.hint"
						:persistent-hint="field.persistent_hint"
						v-model="model[field.key]"
						:disabled="field.disabled"
						clearable
						:menu-props="{'offset-y': true}"
						dense
						outlined
					></v-select>
				</template>
				

				<v-text-field v-else-if="field.type === 'password'"
					:label="field.required ? field.label + '*' : field.label"
					:name="field.key"
					v-model="model[field.key]"
					:item-text="field.item_text ? field.item_text : 'text' "
					:item-value="field.item_value ? field.item_value : 'value'"
					v-validate="field.validations ? field.validations : ''"
					:error-messages="errors.collect(field.label)"
					:data-vv-name="field.label"
					:append-icon="e1 ? 'mdi-eye' : 'mdi-eye-off'"
					@click:append="() => {e1 = !e1}"
					:type="!e1 ? 'password' : 'text'"
					:ref="field.key"
					autocomplete="new-password"
					outlined
					dense>
					<template v-slot:append-outer v-if="field.generate">
						<v-tooltip bottom class="mt-0">
							<template v-slot:activator="{ on }">
								<v-btn icon class="mt-0">
									<v-icon v-on="on" @click="generatePasswordDialog = true">
										mdi-lock-plus-outline
									</v-icon>
								</v-btn>
							</template>
							<span>Genera Password</span>
						</v-tooltip>
						<v-dialog v-model="generatePasswordDialog" width="800">
							<v-card class="background-2 round-card">
								<v-card-title class="pa-3">
									<p class="pl-2 pl-xl-3">Generatore Password</p>
								</v-card-title>
								<v-divider></v-divider>
								<v-card-text>
									<v-container>
										<v-row class="ma-0">
											<v-col class="col-12 pb-0">
												<v-text-field
													type="number"
													label="Lunghezza"
													v-model.number="passwordGeneratorModel['length']"
													:error-messages="errors.collect('Lunghezza')"
													v-validate="'required'"
													data-vv-name="Lunghezza"
													color="primary"
													outlined
													dense
													ref="Lunghezza"
												></v-text-field>
											</v-col>
											<v-col class="col-12 py-0">
												<v-checkbox
													label="Lower Case"
													v-model="passwordGeneratorModel['lower_case']"
													color="primary"
													:input-value="[true,false]"
													ref="Lower Case"
													hide-details
												></v-checkbox>
											</v-col>
											<v-col class="col-12 py-0">
												<v-checkbox
													label="Upper Case"
													v-model="passwordGeneratorModel['upper_case']"
													color="primary"
													:input-value="[true,false]"
													ref="Upper Case"
													hide-details
												></v-checkbox>
											</v-col>
											<v-col class="col-12 py-0">
												<v-checkbox
													label="Caratteri Speciali"
													v-model="passwordGeneratorModel['special_character']"
													color="primary"
													:input-value="[true,false]"
													ref="Caratteri Speciali"
													hide-details
												></v-checkbox>
											</v-col>
											<v-col class="col-12 py-0">
												<v-checkbox
													label="Numeric"
													v-model="passwordGeneratorModel['numeric']"
													color="primary"
													:input-value="[true,false]"
													ref="Numeric"
													hide-details
												></v-checkbox>
											</v-col>
										</v-row>
									</v-container>
								</v-card-text>
								<v-divider></v-divider>
								<v-card-actions>
									<v-spacer></v-spacer>
									<v-btn color="error" text @click="generatePasswordDialog = false">
										Chiudi
									</v-btn>
									<v-btn color="primary" text @click="generatePassword(field.key)">
										Genera
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-dialog>
            		</template>
				</v-text-field>
				<template v-else-if="field.type === 'password_confirmation'">
					<v-text-field  v-if="model.password" 
						:label="field.label"
						:name="field.key"
						v-model="model[field.key]"
						:error-messages="errors.collect(field.label)"
						:data-vv-name="field.label"
						v-validate="'confirmed:password|required'"
						:append-icon="e2 ? 'visibility' : 'visibility_off'"
						@click:append="() => {e2 = !e2}"
						:type="!e2 ? 'password' : 'text'"
						:ref="field.key"
						autocomplete="cpsw"
						outlined
						dense
					></v-text-field>
				</template>

				<template v-else-if="field.type === 'stepper'">
					<v-stepper v-if="$vuetify.breakpoint.mdAndUp && !multipleEdit"
						:value="support[field.key+'_step']" 
						alt-labels 
						class="w100p elevation-0 background-2 c-stepper"
						@change="setValueStepper(field, $event)"
						:ref="field.key"
						>
						<v-stepper-header>
							<template v-for="(option, o) in field.options">
								<v-stepper-step
									class="fs13"
									:step="o + 1"
									editable
									:key="o"
									>{{ option.text }}
								</v-stepper-step>
									<v-divider :key="'divider_'+o" v-if="o + 1 !== field.options.length "></v-divider>
							</template>
						</v-stepper-header>
					</v-stepper>
					<v-autocomplete v-else
						:label="field.required ? field.label + '*' : field.label"
						:items="field.options"
						v-model="model[field.key]"
						:ref="field.key"
						small-chips
						dense
						outlined
					></v-autocomplete>
				</template>

				<template v-else-if="field.type === 'date'">

					<!-- il :ref serve ai chiamanti esterni che fanno
						 $refs.fieldsgenerator.$refs[key][0] senza guardia (es. GenerateInterventionButton) -->
					<gmh-date-picker
						:ref="field.key"
						:validator="$validator"
						:value="model[field.key]"
						@input="(val) => setDateValue(field.key, val)"
						@clear="clearDate(field.key)"
						:label="isRequired(field) ? field.label + '*' : field.label"
						:hint="field.hint"
						:persistent-hint="field.persistent_hint"
						:suffix="field.suffix ? field.suffix : ''"
						:disabled="checkDisable(field)"
						:clearable="typeof field.clearable != 'undefined' ? field.clearable : true"
						:min="getDatePickerMin(field)"
						:max="getDatePickerMax(field)"
						editable
						:validation="formatValidation(field)"
						:vv-name="field.label"
						:vv-validate-on="field['data-vv-validate-on'] ? field['data-vv-validate-on'] : 'input'"
						:vv-delay="field['data-vv-delay'] ? field['data-vv-delay'] : 0"
						:error-messages="errors.collect(field.label)"
					></gmh-date-picker>

				</template>

				<template v-else-if="field.type === 'time'">
					<v-menu
						:ref="field.key"
						:close-on-content-click="false"
						v-model="timepicker_menu[field.key]"
						:nudge-right="40"
						:return-value.sync="model[field.key]"
						transition="scale-transition"
						offset-y
						offset-overflow
						max-width="290px"
						min-width="290px"
						style="width: 100%"
						>
						<template v-slot:activator="{ on }">
							<v-text-field
								v-on="on"
								:label="field.required ? field.label + '*' : field.label"
								append-icon="access_time"
								readonly
								v-model="model[field.key]"
								:suffix="field.suffix ? field.suffix : ''"
								:error-messages="errors.collect(field.label)"
								v-validate="field.validations ? field.validations : ''"
								:data-vv-name="field.label"
								clearable
								@click:clear="model[field.key] = ''"
								dense
							></v-text-field>
						</template>
						<v-time-picker v-if="timepicker_menu[field.key]"
							v-model="model[field.key]" 
							:allowed-minutes="allowedStep"
							@change="$refs[field.key][0].save(model[field.key])"
							format="24hr"
						></v-time-picker>
					</v-menu>
				</template>

				<template v-else-if="field.type === 'datetime'">
					<gmh-date-picker
						:ref="field.key"
						:validator="$validator"
						type="datetime"
						:value="model[field.key]"
						@input="(val) => setDatetimeValue(field.key, val)"
						@clear="clearDatetime(field.key)"
						:label="field.required ? field.label + '*' : field.label"
						append-icon="mdi-calendar-clock"
						:hint="field.hint"
						:persistent-hint="field.persistent_hint"
						:disabled="checkDisable(field)"
						:min="getDatePickerMin(field)"
						:max="getDatePickerMax(field)"
						:allowed-minutes="allowedStep"
						editable
						:validation="formatValidation(field)"
						:vv-name="field.label"
						:error-messages="errors.collect(field.label)"
					></gmh-date-picker>
				</template>

				<template v-else-if="field.type === 'boolean'">
					<v-switch v-if="!multipleEdit"
						class="text-center ma-0 py-0 col-1 text-no-wrap"
						inset
						:label="field.label"
						:hint="field.hint"
						:persistent-hint="field.persistent_hint ? field.persistent_hint : false"
						v-model="model[field.key]"
						:disabled="field.disabled"
						:false-value="field.falseValue ? field.falseValue : false"
						:true-value="field.trueValue ? field.trueValue : true"
						color="primary"
						:ref="field.key"
					></v-switch>
					<v-select
						v-else
						:items="[{'text': 'Si', 'value': true}, {'text': 'No', 'value': false}]"
						:label="field.label"
						:hint="field.hint"
						v-model="model[field.key]"
						:disabled="field.disabled"
						clearable
						:menu-props="{'offset-y': true}"
						dense
						outlined
					></v-select>
				</template>

				<template v-else-if="field.type === 'simple-select'">
					<v-select
						:label="field.required ? field.label + '*' : field.label"
						:items="field.options"
						:hint="field.hint"
						:value="field.hint"
						v-model="model[field.key]"
						:disabled="field.disabled"
						:error-messages="errors.collect(field.label)"
						v-validate="field.validations ? field.validations : ''"
						:data-vv-name="field.label"						
						dense
						:menu-props="{'offset-y': true}"
						outlined
					></v-select>
				</template>

				<v-text-field v-else-if="field.type == 'number' || field.type == 'currency'"
					type="number"
					:label="field.required ? field.label + '*' : field.label"
					:hint="field.hint"
					v-model.number="model[field.key]"
					:suffix="field.suffix ? field.suffix : (field.type == 'currency' ? '€' : '')"
					:prepend-icon="field.prepend_icon"
					:error-messages="errors.collect(field.label)"
					v-validate="field.validations ? field.validations : ''"
					:data-vv-name="field.label"
					color="primary"
					outlined
					:disabled="checkDisable(field)"
					@input="field.on_change ? quantityChange(field, $event) : () => {}"
					dense
					:ref="field.key"
				></v-text-field>
				
				<template v-else-if="field.type == 'email_variables'" >
					<v-chip v-for="(value, key, i) in field.variables" outlined color="primary" class="ma-1" text-color="primary" @click="setTemplateVariable(value)" :key="i">
							{{ value }}
						<v-icon right>mdi-content-copy</v-icon>
					</v-chip>
				</template>		

				<v-treeview v-else-if="field.type == 'treeview'"
					v-model="model[field.key]"
					:ref="field.key"
					:items="field.items"
					:item-text="field.item_text ? field.item_text : 'text' "
					:item-value="field.item_key ? field.item_key : 'value'"
					:activatable="field.activatable"
					active-class="background-2 indigo--text"
					selected-color="primary"
					open-on-click
					selectable
					expand-icon="expand_more"
					on-icon="check_box"
					off-icon="check_box_outline_blank"
					indeterminate-icon="indeterminate_check_box"
				></v-treeview>

				<template v-else-if="field.type == 'signature'" >
					<v-card class="background-2 flex" :ref="field.key">
						<VueSignaturePad
							height="400px"
							width="100%"
							ref="signaturePad"
							saveType="image/png"
						/>
						<v-card-actions>
							<v-spacer></v-spacer>
							<v-btn @click="undo" color="primary" text>Annulla</v-btn>
							<v-btn @click="deleteSignature" color="red" text>Elimina</v-btn>
						</v-card-actions>
					</v-card>
				</template>		

				<template v-else-if="field.type == 'show_signature'" >
					<v-subheader class="pl-0">{{ field.label }}</v-subheader>
					<v-card class="background-1 flex elevation-0" flat outlined>
						<v-img
							:src="model['signature']"
							height="400px"
							contain
						></v-img>
					</v-card>
				</template>


				<template v-else-if="field.type == 'otp'">
					<v-subheader class="pl-0">OTP
						<v-btn icon @click="getOTP(field)" class="ml-6">
							<v-icon>
								{{ showOTP ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
							</v-icon>
						</v-btn>
					</v-subheader>
					<div class="pb-6">
						<template v-if="showOTP">
							<span class="text-h6">
								{{ model['otp']}} 
							</span>
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">											
									<v-btn v-bind="attrs" v-on="on" class="mx-6"
										icon
										small
										@click.native.stop="getCopy(model['otp'])">
										<v-icon color="primary">mdi-content-copy</v-icon>
									</v-btn>
								</template>
								<span>Copia</span>
							</v-tooltip>
							<v-progress-circular
								class="px-6"
								:rotate="360"
								:size="37"
								:width="3"
								:value="progressPercentage"
								color="primary">
								{{ elapsedTime }}
							</v-progress-circular>		
						</template>
					</div>
				</template>

				<file-uploader class="pt-4 pb-4 pa-0" v-else-if="field.type == 'file-uploader'"
					:id.sync="model.id"
					:attachable-type="field.attachableType"
					:options="field.options ? field.options : {}"
					:field="field"
				></file-uploader>

				<v-file-input v-else-if="field.type == 'file'"
					:label="field.required ? field.label + '*' : field.label"
					v-model="model[field.key]"
					outlined dense
					:accept="field.accept"
					small-chips
					:error-messages="errors.collect(field.label)"
					v-validate="field.validations ? field.validations : ''"
					:data-vv-name="field.label"
					:ref="field.key">
				</v-file-input>

				<div v-else-if="field.type == 'space'">
					
				</div>

				<template v-else-if="field.type == 'duplicates-select'">
					<duplicates-select
						:label="field.required ? field.label + '*' : field.label"
						:items="field.options"
						:ref="field.key"
						v-model="model[field.key]"
					></duplicates-select>
				</template>

				<template v-else-if="field.type == 'duration'">
					<v-col class="d-flex" cols="12">
						<v-text-field
							:type="field.type ? field.type : 'text'"
							:label="field.required ? field.label + '*' : field.label"
							:hint="field.hint"
							:disabled="checkDisable(field)"
							v-mask="field.mask"
							v-model="model[field.key]"
							:suffix="field.suffix ? field.suffix : ''"
							:error-messages="errors.collect(field.label)"
							v-validate="field.validations ? field.validations : ''"
							:data-vv-name="field.label"
							dense
							:ref="field.key"
							>
							<template v-slot:prepend-inner>
								<v-btn icon>
									<v-icon @click="addShowDuration(field.key)">add</v-icon>
								</v-btn>
							</template>
						</v-text-field>
						<v-text-field
							class="pl-3"
							v-show="showAddDuration"
							:type="'text'"
							:hint="'Incremento ' + field.label"
							v-mask="field.mask"
							v-model="durations[field.key]"
							:error-messages="errors.collect(field.label + '_add')"
							v-validate="field.add_validations ? field.add_validations : ''"
							:data-vv-name="field.label + '_add'"
							dense>
							<template v-slot:prepend-inner>
								<v-btn icon>
									<v-icon @click="addDuration(field.key)">check</v-icon>
								</v-btn>
							</template>
						</v-text-field>
					</v-col>
				</template>

				<template v-else-if="field.type == 'text-mask'">
					<v-text-field
						:type="field.type ? field.type : 'text-mask'"
						:label="field.required ? field.label + '*' : field.label"
						:hint="field.hint"
						:disabled="checkDisable(field)"
						v-model="model[field.key]"
						:suffix="field.suffix ? field.suffix : ''"
						:error-messages="errors.collect(field.label)"
						v-validate="formatValidation(field)"
						:data-vv-validate-on="field['data-vv-validate-on'] ? field['data-vv-validate-on'] : 'input'"
						:data-vv-delay="field['data-vv-delay'] ? field['data-vv-delay'] : 0"
						:data-vv-name="field.label"
						outlined
						dense
						v-mask="field.mask"
						:return-masked-value="field.returnMaskedValue"						
						:ref="field.key"
					></v-text-field>
				</template>

				<template v-else-if="field.type === 'color-picker'">
					<v-subheader>{{ field.label }}</v-subheader>
						<yello-color-picker
							v-model="model[field.key]"
							:field="field"
							:ref="field.key"
						></yello-color-picker>		
				</template>

				<template v-else-if="field.type === 'pivot'">
					<fieldspivot-generator
						:field="field"
						:model="model"
						:ref="field.key"
					></fieldspivot-generator>
				</template>

				<template v-else-if="field.type === 'hourly-rates-editor'">
					<hourly-rates-editor
						:field="field"
						:model="model"
						:ref="field.key"
					></hourly-rates-editor>
				</template>

				<template v-else-if="field.type === 'lot-allocations-editor'">
					<lot-allocations-editor
						:field="field"
						:model="model"
						:ref="field.key"
					></lot-allocations-editor>
				</template>

				<template v-else-if="field.type === 'tel'">
					<div class="d-flex align-center phone-field-wrapper">
						<v-autocomplete
							v-model="phonePrefixes[field.key]"
							:items="phoneCountryCodesFormatted"
							item-text="code"
							item-value="code"
							:menu-props="{maxHeight: 400, offsetY: true}"
							:disabled="checkDisable(field)"
							outlined
							single-line
							dense
							label="Prefisso"
							class="phone-prefix-select flex-grow-0"
							style="max-width: 110px; min-width: 100px"
							@change="onPhonePrefixChange(field.key)">
							<template v-slot:selection="{ item }">
								<span class="text-body-2" style="white-space: nowrap;">{{ item.flag }} {{ item.code }}</span>
							</template>
							<template v-slot:item="{ item }">
								<v-list-item-avatar size="28" class="my-0 mr-2">
									<span style="font-size: 20px">{{ item.flag }}</span>
								</v-list-item-avatar>
								<v-list-item-content>
									<v-list-item-title class="text-body-2" style="white-space: nowrap;">
										<strong>{{ item.code }}</strong> {{ item.name }}
									</v-list-item-title>
								</v-list-item-content>
							</template>
						</v-autocomplete>

						<v-text-field
							type="tel"
							:label="field.required ? field.label + '*' : field.label"
							:hint="field.hint"
							:persistent-hint="field.persistent_hint"
							:disabled="checkDisable(field)"
							v-model="phoneNumbers[field.key]"
							:error-messages="errors.collect(field.label)"
							v-validate="formatValidation(field)"
							:data-vv-validate-on="field['data-vv-validate-on'] ? field['data-vv-validate-on'] : 'input'"
							:data-vv-delay="field['data-vv-delay'] ? field['data-vv-delay'] : 0"
							:data-vv-name="field.label"
							outlined
							dense
							class="ml-2 flex-grow-1"
							placeholder="Es: 3331234567"
							@input="onPhoneNumberChange(field.key)"
							:ref="field.key">
						</v-text-field>
					</div>
				</template>

				<template v-else>
					<v-text-field
						:type="field.type ? field.type : 'text'"
						:label="field.required ? field.label + '*' : field.label"
						:hint="field.hint"
						:disabled="checkDisable(field)"
						v-model="model[field.key]"
						:suffix="field.suffix ? field.suffix : ''"
						:error-messages="errors.collect(field.label)"
						v-validate="formatValidation(field)"
						:data-vv-validate-on="field['data-vv-validate-on'] ? field['data-vv-validate-on'] : 'input'"
						:data-vv-delay="field['data-vv-delay'] ? field['data-vv-delay'] : 0"
						:data-vv-name="field.label"
						outlined
						dense
						:ref="field.key">

						<template v-slot:append-outer v-if="!model.id && field.openapi && $store.getters.hasApiPermission">
							<v-tooltip top>
								<template v-slot:activator="{ on }">					
									<v-btn
										@click.native="getDataOpenAPI()"
										:disabled="!(model[field.key] && errors.collect(field.label).length == 0 && model[field.key].length == 11)"
										color="primary"
										icon
										:loading="loadingOpenApi"
										v-on="on">
										<v-icon>mdi-cloud-search-outline</v-icon>
									</v-btn>
								</template>
								<span>Cerca</span>
							</v-tooltip>
						</template>

						<template v-slot:append-outer v-else-if="field.split_iban">
							<v-tooltip top>
								<template v-slot:activator="{ on }">
									<v-btn
										@click.native="splitIban(field)"
										:disabled="!model[field.key] || checkDisable(field)"
										color="primary"
										icon
										v-on="on">
										<v-icon>mdi-call-split</v-icon>
									</v-btn>
								</template>
								<span>Scomponi IBAN (CIN, ABI, CAB, Conto)</span>
							</v-tooltip>
						</template>

						<template v-slot:append-outer v-else-if="field.barcode">
							<v-tooltip top>
								<template v-slot:activator="{ on }">
									<v-btn
										@click.native="openBarcodeDialog()"
										color="primary"
										icon
										v-on="on">
										<v-icon>mdi-barcode</v-icon>
									</v-btn>
								</template>
								<span>Genera Barcode</span>
							</v-tooltip>
						</template>
					</v-text-field>	
	
				</template>
				</v-col>
			</transition>
		</template>
		<v-dialog v-model="showDialog"
			max-width="900px"
			persistent
			:fullscreen="$vuetify.breakpoint.xsOnly">
			<v-card class="background-2 round-card">
                <v-card-title class="pa-3">
				    <p class="pl-2 pl-xl-3">Nuovo</p>
                    <v-spacer></v-spacer>
                </v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<v-container class="px-0" fluid>
						<fields-generator
							:fieldsset.sync="fieldssetNew"
							:model="modelNew"
							ref="dialogfieldsgenerator"
						></fields-generator>
						<div><small class="pl-4">* Indica i campi obbligatori</small></div>
					</v-container>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions class="px-2">
					<v-spacer></v-spacer>

					<v-btn color="primary" text @click.native.stop="closeDialog()">Chiudi</v-btn>
					<v-btn color="info" text @click.native="storeNew()">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="miniShowDialog" class="round-card"
			scrollable
			:fullscreen="$vuetify.breakpoint.smAndDown">
			<v-card class="background-1 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Dettagli</p>
					<v-spacer></v-spacer>
					<v-tooltip left>
						<template v-slot:activator="{ on: tooltip }">					
							<v-btn v-on="{ ...tooltip }" icon @click.native.stop="closeMiniShowDialog()"><v-icon>close</v-icon></v-btn>
						</template>
						<span>Chiudi</span>
    				</v-tooltip>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<show v-if="showId"
						:modelDef="showModelDef"
						:id="showId"
						no-buttons
						topClass=""
					></show>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions class="px-2">
					<v-spacer></v-spacer>

					<v-btn color="primary" text @click.native.stop="closeMiniShowDialog()">Chiudi</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog class="round-card" v-if="chipField"
			v-model="chipDialog"
			width="500">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Modifica</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
                    <v-container>
						<v-form ref="chipForm">
							<v-text-field
								type="text"
								:label="chipField.label"
								v-model="chipData.item.text"
								outlined
								dense
							></v-text-field>
						</v-form>
					</v-container>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="error" text @click="chipDialog = false">
						Chiudi
					</v-btn>
					<v-btn :disabled="!chipData.item" color="primary" text @click="saveChip()">
						Modifica
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="barcodeDialog" max-width="600" persistent class="round-card">
			<v-card class="background-2 round-card">
				<v-card-title primary-title class="subtitle-1 background-2">Generazione Barcode</v-card-title>
				<v-divider></v-divider>
				<v-card-text style="min-height: 100px;" class="pt-4 body-2">
					<p>Seleziona il prefisso da utilizzare per generare il barcode:</p>
					<v-select
						v-model="selectedPrefix"
						:items="$store.getters.getSetting('barcode_prefixes') ? $store.getters.getSetting('barcode_prefixes') : []"
						outlined
						dense
						label="Prefisso"
						class="pt-4"
						:menu-props="{'offset-y': true}"
						:disabled="loadingBarcode"
					></v-select>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click.native.stop="barcodeDialog = false" :disabled="loadingBarcode">
						Annulla
					</v-btn>
					<v-btn 
						text 
						color="info" 
						@click.native.stop="confirmGenerateBarcode()" 
						:loading="loadingBarcode"
						:disabled="!selectedPrefix">
						Genera
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Advanced Search Dialog -->
		<v-dialog v-model="advancedSearchDialog" max-width="1800px" :fullscreen="$vuetify.breakpoint.smAndDown" persistent scrollable class="round-card">
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3 background-1">
					<p class="pl-2 pl-xl-3">Ricerca Avanzata Prodotti</p>
					<v-spacer></v-spacer>
					<v-tooltip left>
						<template v-slot:activator="{ on: tooltip }">					
							<v-btn v-on="{ ...tooltip }" icon @click.native.stop="closeAdvancedSearch()"><v-icon>close</v-icon></v-btn>
						</template>
						<span>Chiudi</span>
    				</v-tooltip>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pa-0">
					<v-container fluid class="pa-4">
						<v-row>
							<!-- Filters Section -->
							<v-col cols="12">
								<v-expansion-panels v-model="filtersExpanded" flat>
									<v-expansion-panel>
										<v-expansion-panel-header>
											<template v-slot:default="{ open }">
												<v-row no-gutters align="center">
													<v-col cols="12" sm="4">
														<v-icon small class="mr-1">mdi-filter</v-icon>
														Filtri di Ricerca
													</v-col>
													<v-col cols="12" sm="8" class="text--secondary" :class="{ 'mt-1': $vuetify.breakpoint.xsOnly }">
														<v-fade-transition leave-absolute>
															<span v-if="open" key="0" class="caption">
																<v-icon x-small color="info" class="mr-1">mdi-information</v-icon>
																<span class="hidden-xs-only">La ricerca si aggiorna automaticamente mentre digiti</span>
																<span class="hidden-sm-and-up">Ricerca automatica</span>
															</span>
															<span v-else key="1" class="caption">
																{{ getFiltersSummary() }}
															</span>
														</v-fade-transition>
													</v-col>
												</v-row>
											</template>
										</v-expansion-panel-header>
										<v-expansion-panel-content>
											<fields-generator
												v-if="advancedSearchFields"
												:fieldsset="advancedSearchFieldsMobile"
												:model="advancedSearchFilters"
												ref="advancedSearchFiltersForm"
											></fields-generator>
											<v-btn color="secondary" @click="clearFilters" class="mt-3">
												<v-icon left>clear</v-icon>
												Pulisci Filtri
											</v-btn>
										</v-expansion-panel-content>
									</v-expansion-panel>
								</v-expansion-panels>
							</v-col>

							<!-- Mobile Tabs -->
							<v-col cols="12" v-if="$vuetify.breakpoint.smAndDown" class="pb-0">
								<v-tabs v-model="advancedSearchMobileTab" centered grow>
									<v-tab>
										<v-icon left small>mdi-package-variant</v-icon>
										Prodotti
									</v-tab>
									<v-tab :disabled="!selectedProduct">
										<v-icon left small>mdi-information</v-icon>
										Dettagli
									</v-tab>
								</v-tabs>
							</v-col>

							<!-- Products Table Section -->
							<v-col cols="12" md="7" v-show="!$vuetify.breakpoint.smAndDown || advancedSearchMobileTab === 0">
								<v-card class="round-card" outlined>
									<v-card-title class="pa-2 background-1">
										<p class="pl-2 pl-xl-3">Prodotti</p>
										<v-spacer></v-spacer>
										<span v-if="loadingAdvancedSearch" class="caption">
											<v-progress-circular size="16" width="2" indeterminate color="primary" class="mr-2"></v-progress-circular>
											Ricerca in corso...
										</span>
										<span v-else-if="advancedSearchData.total" class="caption">
											{{ advancedSearchData.total }} risultati
										</span>
									</v-card-title>
									<v-divider></v-divider>
									<v-data-table v-if="advancedSearchInfo.fieldDefs"
										v-model="selectedProducts"
										:headers="advancedSearchInfo.fieldDefs"
										:items="advancedSearchData.data"
										:options.sync="advancedSearchOptions"
										:server-items-length="advancedSearchData.total"
										:loading="loadingAdvancedSearch"
										:footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
										:mobile-breakpoint="0"
										show-select
										single-select
										item-key="id"
										class="elevation-0 product-table"
										@click:row="selectProduct"
										@item-selected="onProductSelected">
										<template slot="item.data-table-select" slot-scope="{ isSelected, select }">
											<v-simple-checkbox
												:value="isSelected"
												@input="select($event)"
												color="primary"
											></v-simple-checkbox>
										</template>
										<template slot="item.stock" slot-scope="{ item }">
											<span :class="item.stock <= 0 ? 'red--text' : ''">{{ item.stock }}</span>
										</template>
										<template slot="item.price" slot-scope="{ item }">
											{{ item.price }}
										</template>
									</v-data-table>
								</v-card>
							</v-col>

							<!-- Product Details Section -->
							<v-col cols="12" md="5" v-show="!$vuetify.breakpoint.smAndDown || advancedSearchMobileTab === 1">
								<v-card flat v-if="selectedProduct" class="background-1 product-details-card">
									<v-card-title class="pa-3 background-2">
										<v-icon left color="primary">info</v-icon>
										<span class="subtitle-1">Dettagli Prodotto</span>
									</v-card-title>
									<v-divider></v-divider>
									<v-card-text class="pa-3">
										<!-- Product Info -->
										<v-card class="mb-3 background-2" flat>
											<v-card-text class="pa-3">
												<div class="d-flex align-center mb-2">
													<v-icon color="primary" class="mr-2">inventory_2</v-icon>
													<span class="subtitle-1 font-weight-medium">{{ selectedProduct.name }}</span>
												</div>
												<div class="d-flex align-center mb-1">
													<v-icon color="grey" size="20" class="mr-2">qr_code</v-icon>
													<span class="body-2 grey--text">Codice:</span>
													<span class="body-2 ml-1 font-weight-medium">{{ selectedProduct.code }}</span>
												</div>
												<div v-if="selectedProduct.barcode" class="d-flex align-center mb-1">
													<v-icon color="grey" size="20" class="mr-2">qr_code_scanner</v-icon>
													<span class="body-2 grey--text">Barcode:</span>
													<span class="body-2 ml-1 font-weight-medium">{{ selectedProduct.barcode }}</span>
												</div>
												<div v-if="selectedProduct.categoryname" class="d-flex align-center mb-1">
													<v-icon color="grey" size="20" class="mr-2">category</v-icon>
													<span class="body-2 grey--text">Categoria:</span>
													<span class="body-2 ml-1 font-weight-medium">{{ selectedProduct.categoryname }}</span>
												</div>
												<div v-if="selectedProduct.price" class="d-flex align-center">
													<v-icon color="green" size="20" class="mr-2">euro</v-icon>
													<span class="body-2 grey--text">Prezzo:</span>
													<span class="body-2 ml-1 font-weight-medium green--text">{{ selectedProduct.price }}</span>
												</div>
											</v-card-text>
										</v-card>

										<!-- Warehouse Stocks -->
										<v-card class="mb-3 background-2" flat>
											<v-card-subtitle class="pa-3 background-1">
												<v-icon left color="primary">store</v-icon>
												<span class="subtitle-2 font-weight-medium">Giacenze per Magazzino</span>
											</v-card-subtitle>
											<v-divider></v-divider>
											<v-card-text class="pa-0">
												<v-simple-table dense class="background-2">
													<template v-slot:default>
														<thead>
															<tr class="background-1">
																<th class="text-left font-weight-medium">Magazzino</th>
																<th class="text-right font-weight-medium">Giacenza</th>
															</tr>
														</thead>
														<tbody>
															<tr v-for="stock in productStocks" :key="stock.warehouse_id" class="hover-row">
																<td class="py-2">
																	<div class="d-flex align-center">
																		<v-icon color="grey" size="16" class="mr-2">business</v-icon>
																		<span class="body-2">{{ stock.warehouse_name }}</span>
																	</div>
																</td>
																<td class="text-right py-2">
																	<v-chip 
																		small 
																		:color="stock.stock <= 0 ? 'red' : 'green'" 
																		:text-color="stock.stock <= 0 ? 'white' : 'white'"
																		class="font-weight-medium">
																		{{ stock.stock }}
																	</v-chip>
																</td>
															</tr>
														</tbody>
													</template>
												</v-simple-table>
											</v-card-text>
										</v-card>

										<!-- Movements -->
										<v-card class="background-2" flat>
											<v-card-subtitle class="pa-3 background-1">
												<v-icon left color="primary">swap_horiz</v-icon>
												<span class="subtitle-2 font-weight-medium">Ultimi Movimenti</span>
											</v-card-subtitle>
											<v-divider></v-divider>
											<v-card-text class="pa-0">
												<v-data-table
													:headers="movementsInfo.fieldDefs"
													:items="productMovements.data"
													:options.sync="movementsOptions"
													:server-items-length="productMovements.total"
													:loading="loadingMovements"
													:footer-props="{ itemsPerPageOptions: [5, 10, 15] }"
													dense
													class="elevation-0 background-2"
													hide-default-header
													no-data-text="Nessun movimento trovato"
												>
													<template slot="header" slot-scope="{ props }">
														<thead class="background-1">
															<tr>
																<th v-for="header in props.headers" 
																	:key="header.value" 
																	:class="header.class + ' font-weight-medium'"
																	style="font-size: 12px;">
																	{{ header.text }}
																</th>
															</tr>
														</thead>
													</template>
													<template slot="item.date" slot-scope="{ item }">
														<span class="body-2">{{ $eventBusUtility.formatDate(item.date) }}</span>
													</template>
													<template slot="item.quantity" slot-scope="{ item }">
														<v-chip 
															x-small 
															:color="item.quantity < 0 ? 'red' : 'green'" 
															text-color="white"
															class="font-weight-medium">
															{{ item.quantity > 0 ? '+' : '' }}{{ item.quantity }}
														</v-chip>
													</template>
													<template slot="item.movement_type_text" slot-scope="{ item }">
														<span class="body-2">{{ item.movement_type_text }}</span>
													</template>
													<template slot="item.causal_text" slot-scope="{ item }">
														<span class="body-2">{{ item.causal_text }}</span>
													</template>
													<template slot="item.warehouse" slot-scope="{ item }">
														<div class="d-flex align-center">
															<v-icon color="grey" size="14" class="mr-1">business</v-icon>
															<span class="body-2">{{ item.warehouse }}</span>
														</div>
													</template>
													<template slot="item.lot" slot-scope="{ item }">
														<span class="body-2">{{ item.lot || '-' }}</span>
													</template>
												</v-data-table>
											</v-card-text>
										</v-card>
									</v-card-text>
								</v-card>
								<v-card flat v-else class="background-1">
									<v-card-text class="text-center pa-6">
										<v-icon size="80" color="grey lighten-1" class="mb-3">search</v-icon>
										<h4 class="grey--text mb-2">Nessun prodotto selezionato</h4>
										<p class="grey--text body-2">Seleziona un prodotto dalla tabella per visualizzare i dettagli, le giacenze e i movimenti</p>
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>
					</v-container>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions class="px-4 pt-2 background-1" :style="{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }">
					<v-spacer></v-spacer>
					<v-btn color="secondary" text @click.native.stop="closeAdvancedSearch()">Chiudi</v-btn>
					<v-btn
						color="primary"
						text
						@click.native="selectAndClose()"
						:disabled="!selectedProduct">
						Seleziona
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
	import Vue from 'vue'
	import InputMixin from '@/mixins/InputMixin'
	import MailTemplateMixin from '@/mixins/MailTemplateMixin'
	import FileUploader from '@/components/common/FileUploader'
	import YelloColorPicker from '@/components/common/YelloColorPicker'
	import DuplicatesSelect from '@/components/common/DuplicatesSelect'
	import FieldspivotGenerator from '@/components/common/FieldspivotGenerator'
	import HourlyRatesEditor from '@/components/common/HourlyRatesEditor'
	import LotAllocationsEditor from '@/components/common/LotAllocationsEditor'
	import CodeEditor from '@/components/common/CodeEditor'
	import WhatsAppTemplateField from '@/components/common/WhatsAppTemplateField'
	import { countryCodes } from '@/utils/countryCodes'
	import { splitItalianIban, hasValidIbanChecksum } from '@/utils/iban'
	
	export default {
		name: 'fields-generator',
		mixins: [
			InputMixin,
			MailTemplateMixin
		],
		components: {
			YelloColorPicker,
			FileUploader,
			DuplicatesSelect,
			FieldspivotGenerator,
			HourlyRatesEditor,
			LotAllocationsEditor,
			CodeEditor,
			WhatsAppTemplateField
		},
		props: {
			layoutClass: Array, 
			fieldsset: Object,
			model: Object,
			files: {
				type: Object,
				default: () => {return new Object()}
			},
			selectChange:  {
				type: Function,
				default: function() {
					return null
				}
			},
			quantityChange:  {
				type: Function,
				default: function() {
					return null
				}
			},
			mefImport: {
				type: Function,
				default: null
			},
			mefImportEnabled: {
				type: Boolean,
				default: false
			},
			support: Object,
			multipleEdit: {
				default: false,
				type: Boolean,
			},
			forceTinymce: {
				default: false,
				type: Boolean,
			}
		},
		data() {
			return {
				oauthLoading: {},
				timepicker_menu: {},
				signatureSelected: {},
				e1: false,
				e2: false,
				cloneFields: this.fieldsset ? this.fieldsset.fields : null,
				fieldIndex: null,
				allowedStep: m => m % 5 === 0,
				fieldssetNew: {},
				modelNew: {},
				showDialog: false,
				miniShowDialog: false,
				showModelDef: null,
				showId: null,
				search: {},
				currentFieldToAdd: {},
				//duration
				showAddDuration: false,
				durations: {},
				chipDialog: false,
				chipField: null,
				chipData: null,
				//otp
				progressPercentage: 100,
				elapsedTime: 30,
				maxTime: 30,
				timer: null,
				showOTP: false,
				generatePasswordDialog: false,
				passwordGeneratorModel: {
					length: 12,
					lower_case: true,
					upper_case: true,
					special_character: true,
					numeric: true,
				},
				loadingOpenApi: false,
				// Campi data modificati dall'utente (per validazione date_min/date_max)
				modifiedDateFields: {},
				// Stato expansion panel gruppi
				groupsExpanded: {},
				currentGroups: [],
				// Cache per performance
				fieldVisibilityCache: new Map(),				
				loadingOpenApi: false,				
				loadingBarcode: false,
				barcodeDialog: false,
				selectedPrefix: null,
				// Advanced Search
				advancedSearchDialog: false,
				advancedSearchField: null,
				advancedSearchFilters: {},
				advancedSearchFields: null,
				advancedSearchInfo: { fieldDefs: [] },
				advancedSearchData: { data: [], total: 0 },
				advancedSearchOptions: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
				selectedProducts: [],
				selectedProduct: null,
				loadingAdvancedSearch: false,
				filtersExpanded: 0,
				searchDebounceTimer: null,
				advancedSearchMobileTab: 0,
				// Product details
				productStocks: [],
				productMovements: { data: [], total: 0 },
				movementsInfo: { fieldDefs: [] },
				movementsOptions: { page: 1, itemsPerPage: 5, sortBy: [], sortDesc: [] },
				loadingMovements: false,
				// Phone fields
				phoneCountryCodes: countryCodes,
				phonePrefixes: {},
				phoneNumbers: {}
			}
		},

		beforeMount() {
			// Inizializza stato dei gruppi collassabili
			this.initializeGroups()
			
			_.forEach(this.fieldsset.fields, (value, key) => {
				if(value.type == 'stepper' && typeof(this.model[value.key]) !== 'undefined'){
					Vue.set(this.support, value.key+'_step', _.findIndex(value.options, (o) => {
						return o.value == this.model[value.key]
					}) + 1)
				}

				if(value.type == 'tel') {
					if(this.model[value.key]) {
						// Parse existing phone number
						this.parsePhoneNumber(value.key, this.model[value.key])
					} else {
						// Initialize with default +39 for new records
						this.initializePhoneField(value.key)
					}
				}
			})
		},

		mounted() {
			this.$eventBusUtility.$on('asyncSearched', (field, search) => {
                this.clearCache(field, search)
            })
			this.$emit('loaded')
		},

		beforeDestroy() {
			this.$eventBusUtility.$off('asyncSearched')
			clearInterval(this.timer)
			if (this.searchDebounceTimer) {
				clearTimeout(this.searchDebounceTimer)
			}
		},

		watch: {
			model: {
				handler() {
					// empties nested depends values that not mathces
					_.forEach(this.fieldsset.fields, (f, key) => {
						if(f.depends_key && typeof f.depends_empty == 'undefined') {
							if(! this.checkDepends(f)) {
								//delete this.model[f.key]
								if(f.type == 'boolean') {
									Vue.set(this.model, f.key, 0)
								} else {
									Vue.set(this.model, f.key, null)
								}
							}
						}
					})
				},
				deep: true
			},

			search: {
				handler() {
					Object.entries(this.search).forEach(([k, s]) => {
						const field = this.fieldsset.fields.find((f) => f.key === k)
						if (field) {
							this.$eventBusUtility.asyncSearch(field, s)
						}
					})
				},
				deep: true
			},

			miniShowDialog: {
				handler(newVal, oldVal) {
					if(!newVal) {
						this.showModelDef = ''
						this.showId = null
					}
				}
			},

			// Add watchers for advanced search
			advancedSearchFilters: {
				handler() {
					if (this.advancedSearchDialog) {
						// Clear existing timer
						if (this.searchDebounceTimer) {
							clearTimeout(this.searchDebounceTimer);
						}
						// Set new timer with 500ms delay
						this.searchDebounceTimer = setTimeout(() => {
							this.searchProducts();
						}, 500);
					}
				},
				deep: true
			},

			advancedSearchOptions: {
				handler() {
					if (this.advancedSearchDialog) {
						this.searchProducts();
					}
				},
				deep: true
			},

			movementsOptions: {
				handler() {
					if (this.selectedProduct) {
						this.loadProductDetails(this.selectedProduct.id);
					}
				},
				deep: true
			}			
		},

		computed: {
			showFields() {
				if(typeof this.fieldsset.fields !== 'undefined') {
					return this.fieldsset.fields.filter((field) => {
						return typeof field.hidden === 'undefined' || !field.hidden
					})
				} else {
					return []
				}
			},

			// Advanced Search fields with full-width cols on mobile
			advancedSearchFieldsMobile() {
				if (!this.advancedSearchFields || !this.advancedSearchFields.fields) {
					return this.advancedSearchFields
				}
				if (!this.$vuetify.breakpoint.smAndDown) {
					return this.advancedSearchFields
				}
				return {
					...this.advancedSearchFields,
					fields: this.advancedSearchFields.fields.map(field => ({
						...field,
						cols: 12
					}))
				}
			},

			checkDisable() {
				return (field) => {
					if(field.disabled == 'start_date') {
					}
					if(typeof field.always_disabled === 'boolean') {
						return field.always_disabled
					} if(typeof field.disabled === 'boolean') {
						return field.disabled
					} else if (typeof field.disabled === 'undefined') {
						return false
					} else if(this.model[field.disabled] != null) {
						return false
					} else {
						return true
					}
				}
			},
			
			// Computed properties per tema e performance
			themeColors() {
				return {
					background1: this.$vuetify.theme.currentTheme['background-1'],
					background2: this.$vuetify.theme.currentTheme['background-2'],
					background3: this.$vuetify.theme.currentTheme['background-3'],
					primary: this.$vuetify.theme.currentTheme.primary
				}
			},
			
			// Mappa precalcolata visibilità campi per performance
			fieldsVisibilityMap() {
				const map = {}
				this.showFields.forEach(field => {
					const isVisible = this.calculateFieldVisibility(field)
					map[field.key] = isVisible
				})
				return map
			},
			
			// Gruppi con stato per gestione ottimizzata
			groupsWithState() {
				return this.currentGroups.map(groupId => ({
					id: groupId,
					expanded: this.groupsExpanded[groupId] || false,
					fieldCount: this.getGroupFieldCount(groupId),
					label: this.getGroupLabel(groupId)
				}))
			},

			// Country codes formatted per autocomplete
			phoneCountryCodesFormatted() {
				return this.phoneCountryCodes
			}
		},


		methods: {
			oauthRedirect(field) {
				if (!field.endpoint) return
				Vue.set(this.oauthLoading, field.key, true)
				this.axios.get(field.endpoint).then((response) => {
					Vue.set(this.oauthLoading, field.key, false)
					const data = response.data || {}
					if (data.url) {
						window.location.href = data.url
					} else {
						alert(data.message || 'Errore: URL OAuth non disponibile')
					}
				}).catch((err) => {
					Vue.set(this.oauthLoading, field.key, false)
					const msg = (err.response && err.response.data && err.response.data.message) || err.message || 'Errore'
					alert(msg)
				})
			},

			initializeGroups() {
				let currentGroupId = null
				this.currentGroups = []
				
				this.fieldsset.fields.forEach((field) => {
					if (field.type === 'label' && field.start) {
						currentGroupId = field.group_id
						
						if (field.collapsible) {
							// Inizializza lo stato del gruppo (espanso se non collapsed_by_default)
							const isExpanded = !field.collapsed_by_default
							Vue.set(this.groupsExpanded, currentGroupId, isExpanded)
							this.currentGroups.push(currentGroupId)
						} else {
							// Gruppo non collassabile, sempre espanso
							Vue.set(this.groupsExpanded, currentGroupId, true)
						}
					}
				})
			},
			
			toggleGroup(groupId) {
				const isExpanding = !this.groupsExpanded[groupId]
				Vue.set(this.groupsExpanded, groupId, isExpanding)
				
				// Invalida cache per campi nel gruppo
				this.invalidateGroupCache(groupId)
				
				// Annuncia lo stato per screen reader
				const groupLabel = this.getGroupLabel(groupId)
				const fieldCount = this.getGroupFieldCount(groupId)
				const message = isExpanding 
					? `${groupLabel} espanso, ${fieldCount} campi disponibili`
					: `${groupLabel} collassato`
				this.announceToScreenReader(message)
			},
			
			invalidateGroupCache(groupId) {
				// Rimuove dalla cache tutti i campi del gruppo
				this.fieldsset.fields.forEach(field => {
					if (field.group_id === groupId) {
						const cacheKey = `${field.key}_${field.group_id}`
						this.fieldVisibilityCache.delete(cacheKey)
					}
				})
			},
			
			isFieldVisible(field) {
				// I campi di tipo label sono sempre visibili
				if (field.type === 'label') return true
				
				// Se il campo non è in un gruppo, è sempre visibile
				if (!field.group_id) return true
				
				// Verifica lo stato del gruppo
				const isExpanded = this.groupsExpanded[field.group_id]
				
				// Se il gruppo non esiste nello stato, assumiamo sia visibile
				if (isExpanded === undefined) return true
				
				return isExpanded
			},
			
			calculateFieldVisibility(field) {
				// Metodo helper per computed property fieldsVisibilityMap
				if (field.type === 'label') return true
				if (!field.group_id) return true
				const isExpanded = this.groupsExpanded[field.group_id]
				return isExpanded === undefined ? true : isExpanded
			},
			
			expandGroupForField(fieldKey) {
				// Trova il campo
				const field = this.fieldsset.fields.find(f => f.key === fieldKey)
				if (field && field.group_id) {
					// Espandi il gruppo se è collassato
					Vue.set(this.groupsExpanded, field.group_id, true)
				}
			},
			
			// Metodi per transizioni dinamiche
			beforeEnter(el) {
				el.style.height = '0'
				el.style.opacity = '0'
			},
			
			enter(el, done) {
				// Calcola altezza reale del contenuto
				const height = el.scrollHeight
				el.style.transition = 'all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)'
				
				// Force reflow per trigger animazione
				el.offsetHeight // eslint-disable-line
				
				el.style.height = height + 'px'
				el.style.opacity = '1'
				
				// Cleanup dopo animazione
				setTimeout(() => {
					el.style.height = 'auto'
					done()
				}, 300)
			},
			
			afterEnter(el) {
				el.style.height = 'auto'
			},
			
			beforeLeave(el) {
				el.style.height = el.scrollHeight + 'px'
				el.style.opacity = '1'
			},
			
			leave(el, done) {
				// Force reflow
				el.offsetHeight // eslint-disable-line
				
				el.style.transition = 'all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)'
				el.style.height = '0'
				el.style.opacity = '0'
				
				setTimeout(done, 300)
			},
			
			// Metodo per ottenere lo stile del header del gruppo con background color
			getGroupHeaderStyle(field) {
				const baseStyle = {}
				
				// Applica il colore di sfondo se presente
				const backgroundHex = this.getBackgroundColorHex(field.background_color)
				if (backgroundHex) {
					baseStyle.backgroundColor = backgroundHex
					// Calcola automaticamente il colore del testo per contrasto
					baseStyle.color = this.getContrastColor(field.background_color)
				}
				return baseStyle
			},
			
			// Helper per estrarre il valore hex dal background_color
			getBackgroundColorHex(background_color) {
				if (!background_color) return null
				
				// Se è già una stringa, usala direttamente
				if (typeof background_color === 'string') return background_color
				
				// Se è un oggetto, estrai la proprietà hex o hexa
				if (typeof background_color === 'object') {
					return background_color.hex || background_color.hexa || null
				}
				
				return null
			},
			
			// Calcola il colore del testo per garantire contrasto
			getContrastColor(hexColor) {
				// Prima estrai il hex se è un oggetto
				const hex = this.getBackgroundColorHex(hexColor)
				if (!hex || typeof hex !== 'string') return null
				
				// Rimuovi # se presente
				const cleanHex = hex.replace('#', '')
				
				// Verifica formato valido
				if (!/^[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(cleanHex)) return null
				
				// Converti in RGB
				const r = parseInt(cleanHex.substring(0, 2), 16)
				const g = parseInt(cleanHex.substring(2, 4), 16)  
				const b = parseInt(cleanHex.substring(4, 6), 16)
				
				// Calcola luminosità usando formula YIQ
				const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000
				
				// Ritorna nero o bianco in base alla luminosità
				return yiq >= 128 ? '#000000' : '#FFFFFF'
			},

			// Ottiene lo stile del titolo con colore di contrasto
			getTitleStyle(field) {
				if (field.background_color) {
					const contrastColor = this.getContrastColor(field.background_color)
					if (contrastColor) {
						return { color: contrastColor }
					}
				}
				return {}
			},

			// Metodi per accessibilità e keyboard navigation
			handleKeyboardNavigation(event, groupId) {
				switch(event.key) {
					case 'Enter':
					case ' ':
						event.preventDefault()
						this.toggleGroup(groupId)
						break
					case 'ArrowDown':
						event.preventDefault()
						this.focusNextGroup(groupId)
						break
					case 'ArrowUp':
						event.preventDefault()
						this.focusPreviousGroup(groupId)
						break
				}
			},
			
			focusNextGroup(currentGroupId) {
				const groups = this.currentGroups
				const currentIndex = groups.indexOf(currentGroupId)
				if (currentIndex < groups.length - 1) {
					const nextGroup = groups[currentIndex + 1]
					const nextHeader = document.querySelector(`[data-group-id="${nextGroup}"]`)
					if (nextHeader) nextHeader.focus()
				}
			},
			
			focusPreviousGroup(currentGroupId) {
				const groups = this.currentGroups
				const currentIndex = groups.indexOf(currentGroupId)
				if (currentIndex > 0) {
					const prevGroup = groups[currentIndex - 1]
					const prevHeader = document.querySelector(`[data-group-id="${prevGroup}"]`)
					if (prevHeader) prevHeader.focus()
				}
			},
			
			getGroupFieldCount(groupId) {
				return this.fieldsset.fields.filter(f => 
					f.group_id === groupId && 
					f.type !== 'label'
				).length
			},
			
			getGroupLabel(groupId) {
				const groupField = this.fieldsset.fields.find(f => 
					f.type === 'label' && 
					f.start && 
					f.group_id === groupId
				)
				return groupField ? groupField.label : ''
			},
			
			announceToScreenReader(message) {
				const announcement = document.createElement('div')
				announcement.setAttribute('aria-live', 'polite')
				announcement.setAttribute('aria-atomic', 'true')
				announcement.style.position = 'absolute'
				announcement.style.left = '-10000px'
				announcement.textContent = message
				document.body.appendChild(announcement)
				
				setTimeout(() => {
					document.body.removeChild(announcement)
				}, 1000)
			},
			
			clearDate(key) {
				Vue.set(this.model, key, '')
				this.applyClearsOnChange(key)
			},

			// required statico (field.required) o condizionale (required_if_filled valorizzato)
			isRequired(field) {
				return field.required || (field.required_if_filled && !!this.model[field.required_if_filled])
			},

			// Svuota i campi elencati in extra_fields.clears_on_change del campo trigger
			applyClearsOnChange(fieldKey) {
				const trigger = this.fieldsset.fields.find(f => f.key === fieldKey)
				if(!trigger || !Array.isArray(trigger.clears_on_change)) return
				trigger.clears_on_change.forEach(targetKey => {
					Vue.set(this.model, targetKey, null)
					Vue.set(this.modifiedDateFields, targetKey, false)
				})
			},

			// Nuovo valore da gmh-date-picker (scelto, digitato o via pulsante "Oggi").
			// modifiedDateFields serve a formatValidation, che aggiunge date_min/date_max
			// solo dopo che il campo e' stato toccato.
			setDateValue(key, val) {
				Vue.set(this.model, key, val)
				Vue.set(this.modifiedDateFields, key, true)
				this.applyClearsOnChange(key)
			},

			// I datetime NON propagano clears_on_change: il vecchio updateDateTime non lo faceva
			// e svuotare i campi dipendenti al cambio dell'ora farebbe perdere dati gia' compilati.
			setDatetimeValue(key, val) {
				Vue.set(this.model, key, val)
				Vue.set(this.modifiedDateFields, key, true)
			},

			getDatePickerMin(field) {
				if (!field.min) return undefined

				// Gestisce i valori speciali per le date
				if (field.min === 'today') {
					const today = new Date()
					return today.toISOString().substring(0, 10)
				}

				if (field.min === 'tomorrow') {
					const tomorrow = new Date()
					tomorrow.setDate(tomorrow.getDate() + 1)
					return tomorrow.toISOString().substring(0, 10)
				}

				if (field.min === 'yesterday') {
					const yesterday = new Date()
					yesterday.setDate(yesterday.getDate() - 1)
					return yesterday.toISOString().substring(0, 10)
				}

				// Se è una chiave del model, restituisce il valore
				return this.model[field.min] || undefined
			},

			getDatePickerMax(field) {
				if (!field.max) return undefined

				// Gestisce i valori speciali per le date
				if (field.max === 'today') {
					const today = new Date()
					return today.toISOString().substring(0, 10)
				}

				if (field.max === 'tomorrow') {
					const tomorrow = new Date()
					tomorrow.setDate(tomorrow.getDate() + 1)
					return tomorrow.toISOString().substring(0, 10)
				}

				if (field.max === 'yesterday') {
					const yesterday = new Date()
					yesterday.setDate(yesterday.getDate() - 1)
					return yesterday.toISOString().substring(0, 10)
				}

				// Se è una chiave del model, restituisce il valore
				return this.model[field.max] || undefined
			},

			clearDatetime(key) {
				Vue.set(this.model, key, null)
			},

			getDataOpenAPI() {

				this.loadingOpenApi = true
				this.axios.post('companies-getdata-openapi/', this.model).then(response => {
					_.forEach(response.data.data, (value, key) => {
						this.$set(this.model, key, value)
					})
					this.loadingOpenApi = false
				}, response => {
					this.loadingOpenApi = false
				})
			},

			splitIban(field) {
				const parts = splitItalianIban(this.model[field.key])
				if (!parts) {
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'IBAN non riconosciuto: la scomposizione è disponibile per IBAN italiani (27 caratteri)', value: true, color: 'error'})
					return
				}
				if (!hasValidIbanChecksum(parts.iban)) {
					this.$store.commit('setSnackbar', {timeout: 6000, text: 'IBAN non valido: le cifre di controllo non corrispondono', value: true, color: 'error'})
					return
				}
				this.$set(this.model, field.key, parts.iban)
				_.forEach(['cin', 'abi', 'cab', 'bank_account'], key => {
					this.$set(this.model, key, parts[key])
				})
				this.$store.commit('setSnackbar', {timeout: 4000, text: 'IBAN scomposto: CIN, ABI, CAB e Conto Corrente compilati', value: true, color: 'success'})
			},

			openBarcodeDialog() {
				this.barcodeDialog = true
			},

			confirmGenerateBarcode() {
				this.loadingBarcode = true
				this.axios.post('products-generate-barcode/', {
					ids: [this.model.id],
					prefix: this.selectedPrefix,
					skip_update: true,
				}).then(response => {
					this.$set(this.model, 'barcode', response?.data?.data[0])
					this.loadingBarcode = false
					this.barcodeDialog = false
				}, response => {
					this.loadingBarcode = false
				})
			},

			getOTP(field) {
				this.showOTP = !this.showOTP
				this.axios.get(field.url + '/' + this.model.id).then(response => {
					this.model.otp = response.data.data

				}, response => {
					// error callback
				})
				this.timer = setInterval(() => {
					this.elapsedTime -= 1
					this.progressPercentage = (this.elapsedTime / this.maxTime) * 100

					if (this.elapsedTime === 0) {
						// Fai la chiamata API qui
						this.axios.get(field.url + '/' + this.model.id).then(response => {
							this.model.otp = response.data.data

						}, response => {
							// error callback
						})

						// Resetta il timer
						this.elapsedTime = this.maxTime
          				this.progressPercentage = 100
					}
				}, 1000) // Intervallo del timer di 1 secondo
			},

			getCopy(value) {
				let tempInput = document.createElement('input')
				tempInput.value = value
				document.body.appendChild(tempInput)
				tempInput.select()
				document.execCommand('copy')
				document.body.removeChild(tempInput)
				this.$store.commit('setSnackbar', {timeout: 6000, text: 'Copiato negli appunti!', value: true, color: 'success'})
			},

			editChip(field, data) { 
				this.chipDialog = true
				this.chipField = field
				this.chipData = data
			},

			saveChip() {
				this.model[this.chipField['key']][this.chipData.index] = this.chipData.item
				this.chipField = null
				this.chipData = null
				this.chipDialog = false
			},

			clearCache({field, search}) {
				if(typeof this.$refs[field.key] !== 'undefined' && typeof this.$refs[field.key][0] !== 'undefined') {
					let old = null
					if(this.model[field.key]) {
						old = this.$refs[field.key][0].cachedItems.find((opt) => opt[field.item_value ? field.item_value : 'value'] == this.model[field.key])
					}
					if(old) {
						this.$refs[field.key][0].cachedItems = [old]
					}
				}
			},

			optimizeDataURL(data){
				return new Promise((resolve, reject) => {
					var img = document.createElement("img")
					img.src = data

					img.onload = () => {
						var canvas = document.createElement("canvas")
						var ctx = canvas.getContext("2d")
						ctx.drawImage(img, 0, 0)

						var MAX_WIDTH = 600
						var MAX_HEIGHT = 600
						var width = img.width
						var height = img.height

						if (width > height) {
						if (width > MAX_WIDTH) {
							height *= MAX_WIDTH / width
							width = MAX_WIDTH
						}
						} else {
						if (height > MAX_HEIGHT) {
							width *= MAX_HEIGHT / height
							height = MAX_HEIGHT
						}
						}
						canvas.width = width
						canvas.height = height
						var ctx = canvas.getContext("2d")
						ctx.drawImage(img, 0, 0, width, height)

						var dataurl = canvas.toDataURL("image/jpeg")

						resolve(dataurl)
					}
				})
			},

			checkDepends(field) {
				if(this.multipleEdit){
					return true
				}
				if(typeof field.depends_key !== 'undefined') {
					let value_check = this.model[field.depends_key]
					if(typeof field.depends_extra_fields !== 'undefined') {
						let a = this.fieldsset.fields.filter((f) => {return f.key === field.depends_key})
						if(a[0]) {
							let b = a[0].options.filter((f) => {return  this.model[field.depends_key] === f.value})
							if(b[0] && b[0].extra_fields) {
								value_check = b[0].extra_fields[field.depends_extra_fields]
							}
						}
					}					

					if(typeof field.depends_condition === 'undefined'){
						return value_check == field.depends_value
					} else if(field.depends_condition === 'in'){
						return field.depends_value.includes(value_check)
					} else if(field.depends_condition === 'not_in'){
						return !field.depends_value.includes(value_check)
					} else if(field.depends_condition == 'not_equal') {
						return value_check != field.depends_value
					} else if(field.depends_condition == 'empty') {
						return value_check == null || value_check == ''
					} else if(field.depends_condition == 'not_empty') {
						return value_check != undefined && value_check !== null && value_check !== ''
					}
				} else {
					return true
				}
			},

			openMiniShow(field) {
				this.miniShowDialog = true
				this.showModelDef = field.showModelDef
				this.showId = this.model[field.key]
			},

			closeMiniShowDialog() {
				this.miniShowDialog = false
			},

			addNew(field) {
				this.axios.get(field.add_new_url + '/create', {
					params: {
						field_filters: {
							'quick_add': true
						}
					}
				}).then(response => {
					
					// get body data
					this.currentFieldToAdd = field
					this.$refs[this.currentFieldToAdd.key][0].blur()

					this.model[field.key] = ''
					this.fieldssetNew = response.data
					this.modelNew[field.relation_field] = this.search[field.key]
					// Take only required and set default value
					this.fieldssetNew.fields  = _.filter(response.data.fields.fields, (f) => {
						if(f.default) {
							Vue.set(this.modelNew, f.key, f.default)
						}
						// set values in add_new_related_key in modelNew
						if(field.add_new_related_key && field.add_new_related_key.includes(f.key)) {
							f.disabled = true
							Vue.set(this.modelNew, [f.key], this.model[f.key])
						}
						return true
					})

					this.showDialog = true

				}, response => {
					// error callback
				})
			},

			closeDialog() {
				this.showDialog = false
				this.modelNew = {}
			},

			storeNew() {
                this.$refs.dialogfieldsgenerator.$validator.validateAll().then(result => {
                    if (!result) {
                        return false
                    }
					this.axios.post(this.currentFieldToAdd.add_new_url, this.modelNew).then(response => {

						_.forEach(this.fieldsset.fields, (field, k) => {
							if(field.key === this.currentFieldToAdd.key) {
								this.fieldsset.fields[k].options.push(response.data.option)
							}
						})
						Vue.set(this.model, [this.currentFieldToAdd.key], response.data.id)
						// autocompile product
                        this.$refs[this.currentFieldToAdd.key][0].$emit('change', response.data.id)

						this.closeDialog()

					}, response => {
						// error callback
					})
                }).catch(() => {

                })
			},

			openFile(file) {
				window.open(this.baseurl + file.path, '_blank');
			},

			loadFile(form, field_key) {
				if(form) {
						// this.files[field_key] = form.getAll('files[]')[0]
						if(typeof this.model['files'] === 'undefined')
							this.model['files'] = {}
						this.model['files'][field_key] = form.getAll('files[]')[0]
				} else {
						// delete this.files[field_key]
						delete this.model['files'][field_key]
						this.model[field_key] = null
				}
			},
			

			setTemplateVariable(value) {
				if (this.$refs.tinymceEditor && this.$refs.tinymceEditor.length > 0) {
					this.$refs.tinymceEditor[0].insertContent('{{' + value + '}}')
					return
				}
				let QuillEditor = this.$refs.editor[0].quill
				this.$eventBusUtility.setVaribleTexEditor(QuillEditor, value)
			},

			setValueStepper(field, event) {
				if(typeof event === 'number') {
					Vue.set(this.model, field.key, field.options[event-1].value)
				}
			},

			undo() {
				this.$refs.signaturePad[0].undoSignature()
			},
			
			deleteSignature() {
				this.$refs.signaturePad[0].clearSignature()
			},

			toggle(field) {
				Vue.nextTick(() => {
					if(this.model[field.key] == undefined) {
						Vue.set(this.model, [field.key], [])
					}
					if(this.model[field.key] && this.model[field.key].length > 0) {
						this.model[field.key] = []
					} else {
						_.forEach(field.options, (o) => {
							if(o.barred != undefined && o.barred == false)
								this.model[field.key].push(o)
						})
					}
				})
			},

			addShowDuration(key) {
				if(this.model[key]) {
					this.showAddDuration = true
				}
			},

			addDuration(key) {
				let old_time = this.model[key].split(':')
				old_time[0] = parseInt(old_time[0]) * 60
				old_time[1] = parseInt(old_time[1])

				if(this.durations[key]) {

					let new_time = this.durations[key].split(':')
					new_time[0] = parseInt(new_time[0]) * 60
					new_time[1] = parseInt(new_time[1])
					let total_minutes = old_time[0] + old_time[1] + new_time[0] + new_time[1]

					let h = total_minutes / 60 | 0,
						m = total_minutes % 60 | 0
					let result = ''
					if(h < 9) {
						result = '0' + Math.round(h).toString() + ':' 
					} else {
						result = Math.round(h).toString() + ':'
					}
					if(m < 9) {
						result = result +  '0' + m.toString()
					} else {
						result = result + m.toString()
					}

					this.model[key] = result
					this.durations[key] = ''
					this.showAddDuration = false
				}
			},

			formatValidation(field) {
				let validation = ''
				if(field['data-vv-validate-on']) {
					if(typeof field.validation_document !== 'undefined') {
						validation = field.validations + ',' + this.model.number + ',' + this.model.sectional_id + ',' + (this.model.quotation_date ? this.model.quotation_date : this.model.date) + (this.model.id ? ',' + this.model.id : '')
					} else if(field.key == 'vat_number') {
						validation = field.validations.replace('vat_zone', this.model.vat_zone) + ',' + this.model.id
					} else {
						validation = field.validations + (this.model.id ? (',' + this.model.id) : '')
					}
				} else {
					validation = field.validations ? field.validations : ''
				}

				// Append extra_validations da extra_fields se presente
				if (field.extra_validations) {
					validation = validation
						? validation + '|' + field.extra_validations
						: field.extra_validations
				}

				// Per campi date/datetime con min/max, aggiunge regole date_min/date_max solo se il campo e' stato modificato
				if ((field.type === 'date' || field.type === 'datetime') && this.modifiedDateFields[field.key]) {
					let minDate = this.getDatePickerMin(field)
					let maxDate = this.getDatePickerMax(field)
					if (minDate) {
						validation = validation ? validation + '|date_min:' + minDate : 'date_min:' + minDate
					}
					if (maxDate) {
						validation = validation ? validation + '|date_max:' + maxDate : 'date_max:' + maxDate
					}
				}

				// Per campi number con min/max, aggiunge regole vee-validate min_value/max_value
				if (field.type === 'number' || field.type === 'int') {
					if (field.min !== undefined && field.min !== null) {
						validation = validation ? validation + '|min_value:' + field.min : 'min_value:' + field.min
					}
					if (field.max !== undefined && field.max !== null) {
						validation = validation ? validation + '|max_value:' + field.max : 'max_value:' + field.max
					}
				}

				// required condizionale: obbligatorio se il campo trigger e' valorizzato
				if (field.required_if_filled && this.model[field.required_if_filled]) {
					validation = validation ? validation + '|required' : 'required'
				}

				return validation
			},

			generatePassword(modelKey) {
				let password = "";
				if (
					!this.passwordGeneratorModel.lower_case &&
					!this.passwordGeneratorModel.upper_case &&
					!this.passwordGeneratorModel.numeric &&
					!this.passwordGeneratorModel.special_character
				) {
					password = '';
				} else {
					const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
					const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
					const numericChars = '0123456789';
					const specialChars = '!@#$%^&*()_-+=<>?/';

					let validChars = '';
					let requiredChars = '';

					if (this.passwordGeneratorModel.lower_case) {
						validChars += lowercaseChars;
						requiredChars += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
					}
					if (this.passwordGeneratorModel.upper_case) {
						validChars += uppercaseChars;
						requiredChars += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
					}
					if (this.passwordGeneratorModel.numeric) {
						validChars += numericChars;
						requiredChars += numericChars[Math.floor(Math.random() * numericChars.length)];
					}
					if (this.passwordGeneratorModel.special_character) {
						validChars += specialChars;
						requiredChars += specialChars[Math.floor(Math.random() * specialChars.length)];
					}

					// Fill the rest of the password
					for (let i = requiredChars.length; i < this.passwordGeneratorModel.length; i++) {
						const randomIndex = Math.floor(Math.random() * validChars.length);
						password += validChars[randomIndex];
					}

					// Shuffle the password to mix required characters with random ones
					password = requiredChars + password;
					password = password.split('').sort(() => 0.5 - Math.random()).join('');
				}

				this.$set(this.model, modelKey, password);
				this.generatePasswordDialog = false;
				
			},

			// Advanced Search Methods
			openAdvancedSearch(field) {
				this.advancedSearchField = field;
				this.advancedSearchDialog = true;
				this.advancedSearchFilters = {};
				this.selectedProducts = [];
				this.selectedProduct = null;
				this.productStocks = [];
				this.productMovements = { data: [], total: 0 };
				
				// Load fields and info for advanced search
				this.loadAdvancedSearchInfo();
			},

			closeAdvancedSearch() {
				this.advancedSearchDialog = false;
				this.advancedSearchField = null;
			},

			async loadAdvancedSearchInfo() {
				try {
					// Load search fields and table info
					const [infoResponse, movementsInfoResponse] = await Promise.all([
						this.axios.get('products-advanced-search-info/'),
						this.axios.get('movements-product-info/')
					]);
					
					this.advancedSearchInfo = infoResponse.data.info
					this.advancedSearchFields = infoResponse.data.fields
					this.movementsInfo = movementsInfoResponse.data.info
					
					// Initial search with empty filters
					this.searchProducts();
				} catch (error) {
					this.$store.commit('setSnackbar', {
						text: 'Errore nel caricamento dei dati di ricerca', 
						value: true, 
						color: 'error'
					});
				}
			},

			async searchProducts() {
				this.loadingAdvancedSearch = true;
				try {
					const data = {
						page: this.advancedSearchOptions.page,
						per_page: this.advancedSearchOptions.itemsPerPage,
						filters: this.advancedSearchFilters
					};

					// Add sortBy and sortDesc only if they exist and are not empty
					if (this.advancedSearchOptions.sortBy && this.advancedSearchOptions.sortBy.length > 0) {
						data.sortBy = this.advancedSearchOptions.sortBy
						data.sortDesc = this.advancedSearchOptions.sortDesc
					}

					const response = await this.axios.post('products-advanced-search/', data)
					this.advancedSearchData = response.data.data
				} catch (error) {
					console.error('Search error:', error)
					this.$store.commit('setSnackbar', {
						text: 'Errore nella ricerca prodotti', 
						value: true, 
						color: 'error'
					});
				} finally {
					this.loadingAdvancedSearch = false
				}
			},

			clearFilters() {
				this.advancedSearchFilters = {}
				// La ricerca sarà automaticamente eseguita dal watcher
			},

			selectProduct(product) {
				this.selectedProduct = product
				this.selectedProducts = [product]
				this.loadProductDetails(product.id)
				// Switch to details tab on mobile
				if (this.$vuetify.breakpoint.smAndDown) {
					this.advancedSearchMobileTab = 1
				}
			},

			onProductSelected({ item, value }) {
				if (value) {
					this.selectProduct(item);
				} else {
					// Se deseleziona, reset
					this.selectedProduct = null
					this.selectedProducts = []
					this.productStocks = []
					this.productMovements = { data: [], total: 0 }
				}
			},

			async loadProductDetails(productId) {
				try {
					// Load warehouse stocks and movements
					const [stocksResponse, movementsResponse] = await Promise.all([
						this.axios.get('products-stocks/', { params: { product_id: productId } }),
						this.axios.get('movements-product/', { 
							params: { 
								product_id: productId,
								page: this.movementsOptions.page,
								per_page: this.movementsOptions.itemsPerPage
							} 
						})
					]);
					
					this.productStocks = stocksResponse.data.data;
					this.productMovements = movementsResponse.data.data;
				} catch (error) {
					this.$store.commit('setSnackbar', {
						text: 'Errore nel caricamento dei dettagli prodotto', 
						value: true, 
						color: 'error'
					});
				}
			},

			selectAndClose() {
				if (this.selectedProduct && this.advancedSearchField) {
					// Calculate the product value based on field configuration
					let productValue;
					
					if (this.advancedSearchField.return_object) {
						// Return the entire object
						productValue = this.selectedProduct;
					} else {
						// For belongsTo relations, typically use 'id'
						// For other cases, use the specified item_value or fallback to 'value'
						const valueKey = this.advancedSearchField.item_value || 
							(this.advancedSearchField.type === 'belongsTo' ? 'id' : 'value');
						productValue = this.selectedProduct[valueKey];
					}
					
					// Set the selected product in the original field model
					const fieldKey = this.advancedSearchField.key;
					this.$set(this.model, fieldKey, productValue);
					
					// Populate the autocomplete field options artificially (like in CrudTableRelationship editItem)
					if (this.advancedSearchField.async) {
						const field = this.advancedSearchField;
						let option = {};
						let value_key = field.item_value ? field.item_value : 'value';
						let text_key = field.item_text ? field.item_text : 'text';
						
						console.log('Advanced Search - Building option:', {
							field: field,
							selectedProduct: this.selectedProduct,
							value_key: value_key,
							text_key: text_key,
							productValue: productValue
						});
						
						// Set the main value and text
						option[value_key] = productValue;
						option[text_key] = this.selectedProduct[field.relation_field || 'name'];
						
						// Add searchKeys if defined
						if (typeof field.searchKeys !== 'undefined') {
							field.searchKeys.forEach(searchKey => {
								option[searchKey] = this.selectedProduct[searchKey];
							});
						}
						option['barred'] = false
						
						// Find the field in fieldsset and update its options
						const fieldIndex = this.fieldsset.fields.findIndex(f => f.key === fieldKey);
						
						if (fieldIndex >= 0) {
							console.log('Setting options for field:', fieldKey, 'with option:', option);
							this.$set(this.fieldsset.fields[fieldIndex], 'options', [option]);
							console.log('Options after set:', this.fieldsset.fields[fieldIndex].options);
							
							// Force update of the autocomplete component
							this.$nextTick(() => {
								if (this.$refs[fieldKey] && this.$refs[fieldKey][0]) {
									// Force re-render by updating the component
									this.$refs[fieldKey][0].$forceUpdate();
									
									// Also update cachedItems to include our new option
									if (this.$refs[fieldKey][0].cachedItems) {
										this.$refs[fieldKey][0].cachedItems = [option];
									}
								}
							});
						}
					}
					
					// Clear the search input to allow new searches
					this.$set(this.search, fieldKey, '');
					
					// Simulate the exact behavior of autocomplete @change="selectChange(field, $event)"
					if (this.selectChange && typeof this.selectChange === 'function') {
						this.selectChange(this.advancedSearchField, productValue);
					}
					
					// Close the advanced search dialog
					this.closeAdvancedSearch();
				}
			},

			getFiltersSummary() {
				const filters = this.advancedSearchFilters;
				const activeFilters = [];

				if (filters.code) activeFilters.push(`Codice: ${filters.code}`);
				if (filters.name) activeFilters.push(`Nome: ${filters.name}`);
				if (filters.barcode) activeFilters.push(`Barcode: ${filters.barcode}`);
				if (filters.category_id) activeFilters.push('Categoria selezionata');
				if (filters.warehouse_id) activeFilters.push('Magazzino selezionato');

				return activeFilters.length > 0 ? activeFilters.join(', ') : 'Nessun filtro attivo';
			},

			// Phone number methods
			parsePhoneNumber(fieldKey, phoneValue) {
				if (!phoneValue) {
					Vue.set(this.phonePrefixes, fieldKey, null)
					Vue.set(this.phoneNumbers, fieldKey, '')
					return
				}

				// Check if number starts with '+'
				if (phoneValue.startsWith('+')) {
					// Try to find matching country code
					let matchedCode = null
					let matchedPrefix = null

					// Sort by code length descending to match longer codes first
					const sortedCodes = [...this.phoneCountryCodes].sort((a, b) => b.code.length - a.code.length)

					for (const countryCode of sortedCodes) {
						if (phoneValue.startsWith(countryCode.code)) {
							matchedCode = countryCode
							matchedPrefix = countryCode.code
							break
						}
					}

					if (matchedPrefix) {
						// Extract number without prefix
						const number = phoneValue.substring(matchedPrefix.length).trim()
						Vue.set(this.phonePrefixes, fieldKey, matchedPrefix)
						Vue.set(this.phoneNumbers, fieldKey, number)
					} else {
						// Has '+' but no matching prefix, keep everything in number field
						Vue.set(this.phonePrefixes, fieldKey, null)
						Vue.set(this.phoneNumbers, fieldKey, phoneValue)
					}
				} else {
					// No international prefix detected, keep everything in number field
					Vue.set(this.phonePrefixes, fieldKey, null)
					Vue.set(this.phoneNumbers, fieldKey, phoneValue)
				}
			},

			initializePhoneField(fieldKey) {
				// Set default +39 prefix for new records
				if (!this.model.id) {
					Vue.set(this.phonePrefixes, fieldKey, '+39')
					Vue.set(this.phoneNumbers, fieldKey, '')
				}
			},

			onPhonePrefixChange(fieldKey) {
				// Recompose full number with new prefix
				const prefix = this.phonePrefixes[fieldKey] || ''
				const number = this.phoneNumbers[fieldKey] || ''

				// Se il numero è vuoto, non salvare solo il prefisso
				if (!number || number.trim() === '') {
					Vue.set(this.model, fieldKey, '')
				} else if (prefix && number) {
					Vue.set(this.model, fieldKey, prefix + number)
				} else if (number) {
					Vue.set(this.model, fieldKey, number)
				} else {
					Vue.set(this.model, fieldKey, '')
				}
			},

			onPhoneNumberChange(fieldKey) {
				// Recompose full number with current prefix
				const prefix = this.phonePrefixes[fieldKey] || ''
				const number = this.phoneNumbers[fieldKey] || ''

				// Rimuovi tutti gli spazi dal numero
				const cleanNumber = number.replace(/\s+/g, '')

				// Se il numero è vuoto, non salvare solo il prefisso
				if (!cleanNumber || cleanNumber.trim() === '') {
					Vue.set(this.model, fieldKey, '')
				} else if (prefix && cleanNumber) {
					Vue.set(this.model, fieldKey, prefix + cleanNumber)
				} else if (cleanNumber) {
					Vue.set(this.model, fieldKey, cleanNumber)
				} else {
					Vue.set(this.model, fieldKey, '')
				}
			}
		},
	}
</script>
<style type="text/css" scoped>
	.divider-item {
		min-height: 28px !important;
		pointer-events: none;
	}

	.expansion-panel__header {
		padding-left: 0px !important;
		padding-top: 8px !important;
		padding-bottom: 8px !important;
	}
	
	.ql-editor {
		/* max-height: 350px; */
		overflow-y: scroll;
  		resize: vertical;
	}
	
	/* Advanced Search Product Table */
	.product-table >>> tbody tr {
		cursor: pointer;
	}
	
	.product-table >>> tbody tr:hover {
		background-color: rgba(0, 0, 0, 0.04) !important;
	}

	/* Product Details Section Styling */
	.hover-row:hover {
		background-color: rgba(0, 0, 0, 0.02) !important;
	}

	.product-details-card .v-chip--small {
		height: 22px;
	}

	.product-details-card .v-chip--x-small {
		height: 18px;
		font-size: 11px;
	}

	.product-details-card .v-data-table >>> .v-data-footer {
		border-top: 1px solid rgba(0, 0, 0, 0.12);
	}

	.product-details-card .v-card__subtitle {
		padding-bottom: 0;
	}
	
	/* Stili per expansion panel fittizio con supporto tema */
	.expansion-panel-header {
		display: flex;
		align-items: center;
		min-height: 48px;
		padding: 0 24px;
		cursor: pointer;
		user-select: none;
		border-radius: 4px;
		transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
		position: relative;
		overflow: hidden;
		/* Background color handled by background-2 class */
	}
	
	/* Rimuovere hover con colore fisso, usare overlay con opacità */
	.expansion-panel-header::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: currentColor;
		opacity: 0;
		transition: opacity 0.3s;
		pointer-events: none;
	}
	
	.expansion-panel-header:hover::before {
		opacity: 0.04;
	}
	
	.expansion-panel-header:focus {
		outline: none;
	}
	
	.expansion-panel-header:focus::before {
		opacity: 0.12;
	}
	
	.expansion-panel-header:active::before {
		opacity: 0.16;
	}
	
	/* Focus visible per accessibilità */
	.expansion-panel-header:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}
	
	.expansion-panel-header--expanded {
		margin-bottom: 0;
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}
	
	/* Icona con colore adattivo al tema */
	.expansion-panel-icon {
		transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
	}
	
	.theme--light .expansion-panel-icon {
		color: rgba(0, 0, 0, 0.54);
	}
	
	.theme--dark .expansion-panel-icon {
		color: rgba(255, 255, 255, 0.7);
	}
	
	.expansion-panel-icon--rotated {
		transform: rotate(-180deg);
	}
	
	/* Titolo con colore adattivo al tema */
	.expansion-panel-title {
		font-size: 15px;
		font-weight: 400;
		letter-spacing: 0.0125em;
		flex: 1;
	}
	
	.theme--light .expansion-panel-title {
		color: rgba(0, 0, 0, 0.87);
	}
	
	.theme--dark .expansion-panel-title {
		color: rgba(255, 255, 255, 0.87);
	}
	
	/* Animazione per i campi all'interno dei gruppi */
	.v-col {
		transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
	}
	
	/* Quando un campo viene nascosto/mostrato */
	.v-col[style*="display: none"] {
		opacity: 0;
		transform: translateY(-10px);
	}
	
	/* Margine bottom per l'ultimo campo del gruppo espanso */
	.expansion-panel-header--expanded + .v-col:last-of-type {
		margin-bottom: 8px;
	}
	
	/* Transizioni per expansion/collapse migliorate */
	.expansion-fade-enter-active,
	.expansion-fade-leave-active {
		transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
		overflow: hidden;
		/* Removed fixed max-height */
	}
	
	.expansion-fade-enter,
	.expansion-fade-leave-to {
		opacity: 0;
		transform: translateY(-10px) scaleY(0.95);
		/* Removed max-height: 0 for better animation */
	}
	
	.expansion-field {
		transform-origin: top;
		will-change: transform, opacity;
	}
	
	/* Ottimizzazione per performance */
	.expansion-panel-header {
		will-change: background-color;
		-webkit-tap-highlight-color: transparent;
	}
	
	/* Ripple effect sul click */
	@keyframes ripple {
		to {
			transform: scale(2);
			opacity: 0;
		}
	}
	
	.expansion-panel-header:active::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.1);
		width: 100px;
		height: 100px;
		margin-top: -50px;
		margin-left: -50px;
		top: 50%;
		left: 50%;
		animation: ripple 0.6s;
		opacity: 0;
	}

	/* Phone field with prefix styles */
	.phone-field-wrapper {
		width: 100%;
	}

	.phone-prefix-select {
		flex-shrink: 0;
	}

	.phone-prefix-select >>> .v-input__control {
		min-height: 40px !important;
	}

	.phone-prefix-select >>> .v-select__selections {
		padding-left: 8px;
		padding-right: 0;
	}

	.phone-prefix-select >>> .v-input__append-inner {
		margin-top: 8px !important;
		padding-left: 0 !important;
	}

	/* Miglioramento dropdown list items */
	.phone-prefix-select >>> .v-list-item__avatar {
		min-width: 28px !important;
	}

	.phone-prefix-select >>> .v-list-item__title {
		font-size: 14px !important;
	}
</style>
